import { IconCalendarOff, IconX } from '@tabler/icons-react-native';
import { LoanCard } from 'app/components/loan-card';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Column } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import { localeAwareFormat, startOfMonth } from 'app/lib/date';
import { navigateToLoan } from 'app/screens/root-stack-helper';
import type { Loan } from 'app/types/loan';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, Pressable, ScrollView } from 'react-native';
import { LoanCalendar } from './loan-calendar';
import { loanOccursOnDate } from './loan-calendar-utils';

export function MobileCalendar({
  currentMonth,
  loans,
  onMonthChange,
}: {
  currentMonth: Date;
  loans: Loan[];
  onMonthChange: (date: Date) => void;
}) {
  const { t } = useTranslation('loans');
  const navigation = useNavigation();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const pendingLoanRef = useRef<Loan | null>(null);
  const selectedDateLoans = selectedDate ? loans.filter((loan) => loanOccursOnDate(loan, selectedDate)) : [];

  const changeMonth = (month: Date) => {
    setSelectedDate(null);
    onMonthChange(month);
  };

  return (
    <>
      <ScrollView
        contentContainerStyle={{ padding: 16, paddingBottom: 40 }}
        showsVerticalScrollIndicator={false}
        style={{ flex: 1 }}
      >
        <LoanCalendar
          currentMonth={currentMonth}
          loans={loans}
          onDatePress={(date) => {
            if (startOfMonth(date).getTime() !== currentMonth.getTime()) {
              onMonthChange(startOfMonth(date));
            }
            setSelectedDate(date);
          }}
          onMonthChange={changeMonth}
          selectedDate={selectedDate}
        />
      </ScrollView>

      <BottomSheet
        containerStyle={{ paddingHorizontal: 16 }}
        onClose={() => {
          setSelectedDate(null);

          const pendingLoan = pendingLoanRef.current;
          if (pendingLoan) {
            pendingLoanRef.current = null;
            navigateToLoan(navigation, pendingLoan.id, pendingLoan.listing.id);
          }
        }}
        enableGesturesInScrollView={false}
        open={Boolean(selectedDate)}
      >
        <Column gap="spacing.general.x4" paddingBottom="spacing.general.x4">
          <Pressable
            accessibilityLabel={t('calendar.close')}
            accessibilityRole="button"
            onPress={() => setSelectedDate(null)}
          >
            <IconX />
          </Pressable>
          <Column>
            <Text fontWeight="600" textAlign="center" variant="headerXSmall">
              {selectedDate ? localeAwareFormat(selectedDate, 'PPPP') : ''}
            </Text>
            <Text color="text.secondary" textAlign="center">
              {t('calendar.sheetSummary', { count: selectedDateLoans.length })}
            </Text>
          </Column>

          {selectedDateLoans.length ? (
            <FlatList
              contentContainerStyle={{ gap: 16, paddingBottom: 8 }}
              data={selectedDateLoans}
              keyExtractor={(loan) => loan.id}
              nestedScrollEnabled
              renderItem={({ item: loan }) => (
                <LoanCard
                  {...loan}
                  onPress={(selectedLoan) => {
                    pendingLoanRef.current = selectedLoan;
                    setSelectedDate(null);
                  }}
                />
              )}
              showsVerticalScrollIndicator={false}
              style={{ maxHeight: 520 }}
            />
          ) : (
            <Column
              alignItems="center"
              backgroundColor="surface.primary-neutral"
              borderRadius="radius.lg"
              gap="spacing.general.x2"
              padding="spacing.general.x6"
            >
              <IconCalendarOff size={28} strokeWidth={ICON_STROKE_WIDTH} />
              <Text color="text.secondary" textAlign="center">
                {t('calendar.noActivityOnDate')}
              </Text>
            </Column>
          )}
        </Column>
      </BottomSheet>
    </>
  );
}
